import React, { useEffect, useCallback } from "react";
import "./viewResaleProject.css";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
    viewProjectResale,
    statusProjectResale,
} from "../../Redux/Actions/projectResaleActions";
import MainLayout from "../../Layout/mainLayout";
import LinkResaleDropdown from "./LinkResaleDropdown";

const formatCurrency = (val, currency = "INR") => {
    if (!val && val !== 0) return "—";
    const symbol = currencySymbolMap[currency] || currencySymbolMap.INR;

    if (val >= 10000000) return `${symbol}${(val / 10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `${symbol}${(val / 100000).toFixed(2)} Lacs`;
    return `${symbol}${Number(val).toLocaleString("en-IN")}`;
};

const currencySymbolMap = {
    INR: "₹",
    USD: "$",
    AED: "د.إ",
    EUR: "€",
};

const BoolTag = ({ value }) => (
    <span className={`vrp-bool-tag vrp-bool-tag--${value ? "yes" : "no"}`}>
        {value ? "✓ Yes" : "✗ No"}
    </span>
);

const InfoItem = ({ label, value, full }) => (
    <div className={`vrp-info-item${full ? " vrp-info-item--full" : ""}`}>
        <span className="vrp-info-label">{label}</span>
        <span className="vrp-info-value">{value || "—"}</span>
    </div>
);

const CardSection = ({ badge, title, children }) => (
    <div className="vrp-card">
        <div className="vrp-card-header">
            <span className="vrp-card-badge">{badge}</span>
            <h2 className="vrp-card-title">{title}</h2>
        </div>
        {children}
    </div>
);

const ViewResaleProject = () => {
    const { id } = useParams();
    const dispatch = useDispatch();

    // ✅ No longer subscribes to projectResaleList — only re-renders on detail/loading changes
    const { projectResaleDetail, detailLoading } = useSelector((state) => ({
        projectResaleDetail: state.projectResale?.projectResaleDetail,
        detailLoading: state.projectResale?.detailLoading,
    }));

    const data = projectResaleDetail?.data;

    useEffect(() => {
        if (!id) return;
        dispatch(viewProjectResale(id)).catch(() => {
            toast.error("Failed to load project details.");
        });
    }, [id, dispatch]);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return "—";
        return new Date(dateString).toLocaleDateString("en-IN", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    }, []);

    const statusClass = (status) => {
        const map = {
            published: "vrp-status-badge--published",
            approved: "vrp-status-badge--approved",
            requested: "vrp-status-badge--requested",
            unpublished: "vrp-status-badge--unpublished",
            sold: "vrp-status-badge--sold",
            declined: "vrp-status-badge--declined",
        };
        return map[status?.toLowerCase()] || "vrp-status-badge--unpublished";
    };

    const handleStatusChange = async (e) => {
        const status = e.target.value;
        try {
            const res = await dispatch(statusProjectResale({ id, status }));
            if (res?.statusCode === 200) {
                toast.success(res.message || "Status updated!");
                dispatch(viewProjectResale(id));
            }
        } catch {
            toast.error("Failed to update status");
        }
    };

    const investmentFeatures = [
        { key: "is_assured_return", label: "Assured Return" },
        { key: "is_assured_rental", label: "Assured Rental" },
        { key: "is_preleased_property", label: "Pre-leased Property" },
        { key: "is_high_rental_yield", label: "High Rental Yield" },
        { key: "is_guaranteed_buyback", label: "Guaranteed Buyback" },
        { key: "is_zero_maintenance", label: "Zero Maintenance" },
        { key: "is_fixed_monthly_income", label: "Fixed Monthly Income" },
        { key: "is_fractional_ownership", label: "Fractional Ownership" },
    ];

    if (detailLoading) {
        return (
            <MainLayout>
                <div className="vrp-loader-wrap">
                    <div className="vrp-loader" />
                    <p className="vrp-loader-text">Loading project details…</p>
                </div>
            </MainLayout>
        );
    }

    if (!data) {
        return (
            <MainLayout>
                <div className="vrp-loader-wrap">
                    <p className="vrp-loader-text">Project not found.</p>
                </div>
            </MainLayout>
        );
    }

    const activeInvestmentFeatures = investmentFeatures.filter((f) => data[f.key]);

    return (
        <MainLayout>
            <div className="vrp-wrapper">

                {/* ── Header ── */}
                <div className="vrp-header">
                    <div className="vrp-header-left">
                        <Link to="/resale_project" className="vrp-back-btn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            Back
                        </Link>
                        <div className="vrp-header-divider" />
                        <div>
                            <div className="vrp-breadcrumb">Projects / Resale / View</div>
                            <h1 className="vrp-title">Project Details</h1>
                        </div>
                    </div>
                </div>

                {/* ── Hero ── */}
                <div className="vrp-hero-card">
                    {data.cover_photo
                        ? <img src={data.cover_photo} alt="cover" className="vrp-hero-cover" />
                        : <div className="vrp-hero-cover-placeholder" />
                    }
                    <div className="vrp-hero-overlay">
                        <div className="vrp-hero-badges">
                            <span className={`vrp-status-badge ${statusClass(data.status)}`}>
                                {data.status || "Unknown"}
                            </span>
                            {data.project_type?.name && (
                                <span className="vrp-type-badge">{data.project_type.name}</span>
                            )}
                            {data.is_investment_opportunity && (
                                <span className="vrp-type-badge" style={{ background: "rgba(16,185,129,.25)", borderColor: "rgba(16,185,129,.4)" }}>
                                    Investment Opportunity
                                </span>
                            )}
                            {data.is_recommended && (
                                <span className="vrp-type-badge" style={{ background: "rgba(245,158,11,.2)", borderColor: "rgba(245,158,11,.4)" }}>
                                    ★ Recommended
                                </span>
                            )}
                            {data.is_exclusive && (
                                <span className="vrp-type-badge" style={{ background: "rgba(99,102,241,.25)", borderColor: "rgba(99,102,241,.4)" }}>
                                    Exclusive
                                </span>
                            )}
                        </div>
                        <h2 className="vrp-hero-name">{data.project_name || "Untitled Project"}</h2>
                        <div className="vrp-hero-meta">
                            {(data.city?.city_name || data.place) && (
                                <span className="vrp-hero-meta-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                        <circle cx="12" cy="10" r="3" />
                                    </svg>
                                    {[data.city?.city_name, data.state?.state_name, data.country?.country_name].filter(Boolean).join(", ")}
                                </span>
                            )}
                            {data.construction_status && (
                                <span className="vrp-hero-meta-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="2" y="3" width="20" height="14" rx="2" />
                                        <line x1="8" y1="21" x2="16" y2="21" />
                                        <line x1="12" y1="17" x2="12" y2="21" />
                                    </svg>
                                    {data.construction_status}
                                </span>
                            )}
                            {data.rera_id && (
                                <span className="vrp-hero-meta-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                    </svg>
                                    RERA: {data.rera_id}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Stats Row ── */}
                <div className="vrp-stats-row">
                    <div className="vrp-stat-card">
                        <span className="vrp-stat-label">Starting Price</span>
                        <span className="vrp-stat-value">
                            {formatCurrency(data.starting_price, data.currency || "INR")}
                        </span>
                        <span className="vrp-stat-sub">Base price</span>
                    </div>
                    <div className="vrp-stat-card">
                        <span className="vrp-stat-label">Avg. Price / sq.ft</span>
                        <span className="vrp-stat-value">{formatCurrency(data.average_price)}</span>
                        <span className="vrp-stat-sub">Per sq.ft</span>
                    </div>
                    <div className="vrp-stat-card">
                        <span className="vrp-stat-label">Project Area</span>
                        <span className="vrp-stat-value">{data.project_area || "—"}</span>
                        <span className="vrp-stat-sub">{data.area_unit || ""}</span>
                    </div>
                    <div className="vrp-stat-card">
                        <span className="vrp-stat-label">Status</span>
                        <select
                            value={data.status || ""}
                            onChange={handleStatusChange}
                            style={{
                                marginTop: 6,
                                padding: "6px 10px",
                                borderRadius: 8,
                                border: "1px solid #e4e7ec",
                                fontSize: 13,
                                fontWeight: 600,
                                cursor: "pointer",
                                outline: "none",
                                background: "#f9fafb",
                                color: "#111827",
                                width: "100%",
                            }}
                        >
                            <option value="requested">Requested</option>
                            <option value="approved">Approved</option>
                            <option value="declined">Declined</option>
                            <option value="unpublished">Unpublished</option>
                            <option value="published">Published</option>
                            <option value="sold">Sold</option>
                        </select>
                        <span className="vrp-stat-sub">Change status</span>
                    </div>
                </div>

                {/* ── Two-column Grid ── */}
                <div className="vrp-grid-layout">

                    {/* Left Column */}
                    <div className="vrp-col">

                        {/* ✅ Isolated dropdown — only this re-renders when list loads */}
                        <CardSection badge="★" title="Link Resale Properties">
                            <LinkResaleDropdown
                                id={id}
                                projectTypeId={data.project_type?._id}
                                onLinked={() => dispatch(viewProjectResale(id))}
                                linkedProperties={data.properties_resale || []}
                            />
                        </CardSection>

                        <CardSection badge="01" title="Builder & Basic Info">
                            <div className="vrp-info-grid">
                                <InfoItem label="Builder" value={data.builder?.brand_name || data.brand_name || "—"} />
                                <InfoItem label="Builder ID" value={data.builder?.builder_id} />
                                <InfoItem label="Construction Status" value={data.construction_status} />
                                <InfoItem label="Possession Status" value={data.possesion_status} />
                                <InfoItem label="Launch Date" value={formatDate(data.launch_date)} />
                                <InfoItem label="Possession Date" value={formatDate(data.possesion_date)} />
                                {data.property_age && (
                                    <InfoItem label="Property Age" value={`${data.property_age} ${data.property_age_in || "year(s)"}`} />
                                )}
                                <InfoItem label="Added By" value={data.added_by} />
                            </div>
                        </CardSection>

                        <CardSection badge="02" title="Location">
                            <div className="vrp-info-grid">
                                <InfoItem label="Country" value={data.country?.country_name} />
                                <InfoItem label="State" value={data.state?.state_name} />
                                <InfoItem label="City" value={data.city?.city_name} />
                                <InfoItem label="Place" value={data.place} />
                                <InfoItem label="Street Address" value={data.street_address} full />
                            </div>
                            {data.localities?.length > 0 && (
                                <div style={{ marginTop: 14 }}>
                                    <span className="vrp-info-label" style={{ display: "block", marginBottom: 8 }}>Localities</span>
                                    <div className="vrp-locality-list">
                                        {data.localities.map((loc, i) => (
                                            <span key={i} className="vrp-locality-tag">{loc.name || loc}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {data.location?.coordinates?.length === 2 && (
                                <div className="vrp-map-embed-wrap">
                                    <iframe
                                        title="Project Location"
                                        width="100%"
                                        height="220"
                                        style={{ border: 0, borderRadius: "var(--vrp-radius-lg)", display: "block" }}
                                        loading="lazy"
                                        allowFullScreen
                                        referrerPolicy="no-referrer-when-downgrade"
                                        src={`https://www.google.com/maps?q=${data.location.coordinates[1]},${data.location.coordinates[0]}&z=15&output=embed`}
                                    />
                                </div>
                            )}
                        </CardSection>

                        <CardSection badge="03" title="Area & Pricing">
                            <div className="vrp-info-grid">
                                <InfoItem label="Project Area" value={data.project_area ? `${data.project_area} ${data.area_unit || ""}` : "—"} />
                                <InfoItem label="No. of Towers" value={data.no_of_towers} />
                                <InfoItem label="No. of Floors" value={data.no_of_floors} />
                                <InfoItem label="No. of Plots" value={data.no_of_plots} />
                                <InfoItem label="No. of Parks" value={data.no_of_parks} />
                                <InfoItem label="Green Area %" value={data.green_area_percentage ? `${data.green_area_percentage}%` : "—"} />
                                <InfoItem label="Clubhouse Area" value={data.clubhouse_area ? `${data.clubhouse_area} ${data.clubhouse_area_measure_in || ""}` : "—"} />
                                <InfoItem label="Average Rental" value={data.average_rental} />
                                <InfoItem label="Discount Value" value={data.discount_value ? `${data.discount_value}%` : "—"} />
                                <InfoItem label="Hold Time" value={data.hold_time ? `${data.hold_time} hrs` : "—"} />
                                <InfoItem label="Maintenance" value={data.maintainence} />
                                <InfoItem label="Community" value={data.community} />
                            </div>
                        </CardSection>

                        {data.about_project && (
                            <CardSection badge="04" title="About Project">
                                <p className="vrp-description">{data.about_project}</p>
                            </CardSection>
                        )}

                        {data.about_builder && (
                            <CardSection badge="05" title="About Builder">
                                <p className="vrp-description">{data.about_builder}</p>
                            </CardSection>
                        )}

                        {(data.note || data.payment_note || data.extra_benefits) && (
                            <CardSection badge="06" title="Notes & Benefits">
                                <div className="vrp-info-grid">
                                    {data.extra_benefits && <InfoItem label="Extra Benefits" value={data.extra_benefits} full />}
                                    {data.note && <InfoItem label="Note" value={data.note} full />}
                                    {data.payment_note && <InfoItem label="Payment Note" value={data.payment_note} full />}
                                </div>
                            </CardSection>
                        )}

                        {data.specifications?.length > 0 && (
                            <CardSection badge="07" title="Specifications">
                                <div className="vrp-spec-list">
                                    {data.specifications.map((spec, i) => (
                                        <div key={i}>
                                            <p className="vrp-spec-type">{spec.specification_type}</p>
                                            <div className="vrp-spec-tags">
                                                {spec.sub_specification?.map((sub, j) => (
                                                    <span key={j} className={`vrp-spec-tag ${sub.status === "verified" ? "vrp-spec-tag--verified" : ""}`}>
                                                        {sub.status === "verified" && "✓ "}{sub.name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                    </div>

                    {/* Right Column */}
                    <div className="vrp-col">

                        {data.cover_photo && (
                            <CardSection badge="08" title="Cover Photo">
                                <div className="vrp-cover-wrap">
                                    <img src={data.cover_photo} alt="cover" className="vrp-cover-img" />
                                </div>
                            </CardSection>
                        )}

                        {data.images?.length > 0 && (
                            <CardSection badge="09" title={`Project Images (${data.images.length})`}>
                                <div className="vrp-images-grid">
                                    {data.images.map((img, i) => (
                                        <img key={i} src={img.url || img} alt={`img-${i}`} className="vrp-thumb" />
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {data.is_investment_opportunity && (
                            <CardSection badge="10" title="Investment Features">
                                {activeInvestmentFeatures.length > 0 ? (
                                    <div className="vrp-feature-list">
                                        {activeInvestmentFeatures.map((f) => (
                                            <span key={f.key} className="vrp-feature-tag">✓ {f.label}</span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="vrp-no-data">No investment features selected.</p>
                                )}
                                <div className="vrp-info-grid" style={{ marginTop: 14 }}>
                                    <div className="vrp-info-item">
                                        <span className="vrp-info-label">Assured Return</span>
                                        <BoolTag value={data.is_assured_return} />
                                    </div>
                                    <div className="vrp-info-item">
                                        <span className="vrp-info-label">Assured Rental</span>
                                        <BoolTag value={data.is_assured_rental} />
                                    </div>
                                </div>
                            </CardSection>
                        )}

                        {data.amenities?.length > 0 && (
                            <CardSection badge="11" title={`Amenities (${data.amenities.length})`}>
                                <div className="vrp-amenity-list">
                                    {data.amenities.map((a, i) => (
                                        <span key={i} className="vrp-amenity-tag">
                                            {a.icon && <img src={a.icon} alt={a.name} className="vrp-amenity-icon" />}
                                            {a.name || a}
                                        </span>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {data.brands_nearby?.filter(b => b.name).length > 0 && (
                            <CardSection badge="12" title="Brands Nearby">
                                <div className="vrp-amenity-list">
                                    {data.brands_nearby.filter(b => b.name).map((b, i) => (
                                        <span key={i} className="vrp-amenity-tag">
                                            {b.image && <img src={b.image} alt={b.name} className="vrp-amenity-icon" />}
                                            {b.name}
                                        </span>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {data.project_highlight?.length > 0 && (
                            <CardSection badge="13" title="Project Highlights">
                                <div className="vrp-highlight-list">
                                    {data.project_highlight.map((h, i) => (
                                        <div key={i} className="vrp-highlight-item">
                                            {h.image && <img src={h.image} alt={h.title} className="vrp-highlight-img" />}
                                            <div className="vrp-highlight-body">
                                                <p className="vrp-highlight-title">{h.title}</p>
                                                {h.description && <p className="vrp-highlight-desc">{h.description}</p>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {data.news?.length > 0 && (
                            <CardSection badge="14" title="News">
                                <div className="vrp-news-list">
                                    {data.news.map((n, i) => (
                                        <div key={i} className="vrp-news-item">
                                            {n.image && <img src={n.image} alt={n.title} className="vrp-news-img" />}
                                            <div>
                                                <p className="vrp-news-title">{n.title}</p>
                                                {n.description && <p className="vrp-news-desc">{n.description}</p>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {data.faqs?.length > 0 && (
                            <CardSection badge="15" title={`FAQs (${data.faqs.length})`}>
                                <div className="vrp-faq-list">
                                    {data.faqs.map((faq, i) => (
                                        <div key={i} className="vrp-faq-item">
                                            <p className="vrp-faq-q">Q: {faq.question}</p>
                                            <p className="vrp-faq-a">A: {faq.answer}</p>
                                        </div>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {data.yt_links?.filter(y => y.url).length > 0 && (
                            <CardSection badge="16" title="YouTube Links">
                                <div className="vrp-yt-list">
                                    {data.yt_links.filter(y => y.url).map((y, i) => (
                                        <a key={i} href={y.url} target="_blank" rel="noreferrer" className="vrp-yt-link">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1C4.5 20.5 12 20.5 12 20.5s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1C24 15.9 24 12 24 12s0-3.9-.5-5.8zM9.75 15.5V8.5l6.5 3.5-6.5 3.5z" />
                                            </svg>
                                            {y.url}
                                        </a>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {data.brochure && (
                            <CardSection badge="17" title="Brochure">
                                <a href={data.brochure} target="_blank" rel="noreferrer" className="vrp-brochure-btn">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                        <line x1="12" y1="18" x2="12" y2="12" />
                                        <line x1="9" y1="15" x2="15" y2="15" />
                                    </svg>
                                    Download Brochure (PDF)
                                </a>
                            </CardSection>
                        )}

                        {data.two_d_map?.filter(m => m.image).length > 0 && (
                            <CardSection badge="18" title="2D Maps">
                                <div className="vrp-map-list">
                                    {data.two_d_map.filter(m => m.image).map((m, i) => (
                                        <div key={i} className="vrp-map-item">
                                            <img src={m.image} alt={m.title || `map-${i}`} className="vrp-map-img" />
                                            {(m.title || m.tower_name) && (
                                                <div className="vrp-map-label">
                                                    {m.tower_name && <span>{m.tower_name} — </span>}
                                                    {m.title}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default ViewResaleProject;